import cs from "classnames";
import React from "react";
import { FaTimes } from "react-icons/fa";
import { defineMessages, FormattedMessage } from "react-intl";

import {
	formatDateTimeValue,
	getDateStr,
	getTimeStr,
	parseDateTimeValue
} from "src/common/utils/dates";

import "./DateTimeInput.css";

export const messages = defineMessages({
	now: {
		id: "date_time_input.now",
		defaultMessage: "Most"
	}
});

interface DateTimeInputProps {
	disabled?: boolean;
	value?: string;
	onChangeValue?(value: string | undefined): unknown;
}

interface DateTimeInputState {
	isDateFocused: boolean;
	isTimeFocused: boolean;
	isClearFocused: boolean;
	isNowFocused: boolean;
	value: string | undefined;
	date: string;
	time: string;
}

const emptyState = {
	isDateFocused: false,
	isTimeFocused: false,
	isClearFocused: false,
	isNowFocused: false,
	value: undefined,
	date: "",
	time: ""
};

export class DateTimeInput extends React.PureComponent<
	DateTimeInputProps,
	DateTimeInputState
> {
	constructor(props: DateTimeInputProps) {
		super(props);
		this.state = {
			...emptyState,
			value: this.props.value,
			...parseDateTimeValue(this.props.value)
		};
	}

	componentDidUpdate(prevProps: DateTimeInputProps): void {
		if (
			this.props.value !== prevProps.value &&
			this.props.value !== this.state.value
		) {
			this.setState({
				value: this.props.value,
				...parseDateTimeValue(this.props.value)
			});
		}
	}

	render(): React.ReactElement {
		return (
			<div
				className={cs("date-time", {
					"date-time--disabled": this.props.disabled,
					"date-time--focused": this.isFocused()
				})}
			>
				<input
					className="date-time__input date-time__input--date"
					disabled={this.props.disabled}
					type="date"
					max="9999-12-31"
					value={this.state.date}
					onFocus={this.onFocusDate}
					onBlur={this.onBlurDate}
					onChange={this.onChangeDate}
				/>
				<input
					className="date-time__input date-time__input--time"
					disabled={this.props.disabled}
					type="time"
					step="1"
					value={this.state.time}
					onFocus={this.onFocusTime}
					onBlur={this.onBlurTime}
					onChange={this.onChangeTime}
				/>
				<button
					disabled={this.props.disabled || !this.state.value}
					className="date-time__button date-time__button--clear"
					type="button"
					onFocus={this.onFocusClear}
					onBlur={this.onBlurClear}
					onClick={this.onClickClear}
				>
					<FaTimes />
				</button>
				<button
					disabled={this.props.disabled}
					className="date-time__button date-time__button--now"
					type="button"
					onFocus={this.onFocusNow}
					onBlur={this.onBlurNow}
					onClick={this.onClickNow}
				>
					<FormattedMessage {...messages.now} />
				</button>
			</div>
		);
	}

	private isFocused(): boolean {
		return (
			this.state.isDateFocused ||
			this.state.isTimeFocused ||
			this.state.isClearFocused ||
			this.state.isNowFocused
		);
	}

	private onFocusDate = (): void => {
		this.setState({ isDateFocused: true });
	};

	private onBlurDate = (): void => {
		this.setState({ isDateFocused: false });
	};

	private onFocusTime = (): void => {
		this.setState({ isTimeFocused: true });
	};

	private onBlurTime = (): void => {
		this.setState({ isTimeFocused: false });
	};

	private onFocusNow = (): void => {
		this.setState({ isNowFocused: true });
	};

	private onBlurNow = (): void => {
		this.setState({ isNowFocused: false });
	};

	private onFocusClear = (): void => {
		this.setState({ isClearFocused: true });
	};

	private onBlurClear = (): void => {
		this.setState({ isClearFocused: false });
	};

	private onChangeDate = ({
		target: { value: date }
	}: React.ChangeEvent<HTMLInputElement>): void => {
		this.setState(
			({ time }) => DateTimeInput.getNextState(date, time),
			this.emitOnChangeValue
		);
	};

	private onChangeTime = ({
		target: { value: time }
	}: React.ChangeEvent<HTMLInputElement>): void => {
		this.setState(
			({ date }) => DateTimeInput.getNextState(date, time),
			this.emitOnChangeValue
		);
	};

	private onClickClear = (): void => {
		this.setState(emptyState, this.emitOnChangeValue);
	};

	private onClickNow = (): void => {
		const d = new Date();
		d.setMilliseconds(0);
		const date = getDateStr(d);
		const time = getTimeStr(d);
		this.setState(
			() => DateTimeInput.getNextState(date, time),
			this.emitOnChangeValue
		);
	};

	private emitOnChangeValue = (): void => {
		if (this.props.onChangeValue) {
			this.props.onChangeValue(this.state.value);
		}
	};

	private static getNextState(
		date: string,
		time: string
	): Pick<DateTimeInputState, "value" | "date" | "time"> {
		return { value: formatDateTimeValue(date, time), date, time };
	}
}
